import { useState, useEffect, useCallback } from "react";
import { Bell, Send, MessageCircle, Mail, Users, AlertCircle, CheckCircle, Clock, ExternalLink, Settings, Zap, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function api(token) {
  return axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
}

export default function AdminNotificationsTab({ token }) {
  const [clients, setClients] = useState([]);
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState("");
  const [bulkSending, setBulkSending] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showPush, setShowPush] = useState(false);
  const [pushTitle, setPushTitle] = useState("");
  const [pushBody, setPushBody] = useState("");
  const [pushUrl, setPushUrl] = useState("/dashboard");
  const [pushSending, setPushSending] = useState(false);
  const [pushSubscribers, setPushSubscribers] = useState([]);
  const [pushHistory, setPushHistory] = useState([]);
  const [showDiag, setShowDiag] = useState(false);
  const [diag, setDiag] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const loadData = useCallback(() => {
    api(token).get("/admin/clients").then(r => setClients(r.data)).catch(() => {});
    api(token).get("/notifications/history").then(r => setHistory(r.data)).catch(() => {});
    api(token).get("/notifications/settings").then(r => setSettings(r.data)).catch(() => {});
    api(token).get("/push/subscribers").then(r => setPushSubscribers(r.data)).catch(() => {});
    api(token).get("/push/history").then(r => setPushHistory(r.data)).catch(() => {});
    api(token).get("/notifications/diagnostics").then(r => setDiag(r.data)).catch(() => {});
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const runEmailTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter an email to test"); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api(token).post("/notifications/test-email", { to_email: testEmail.trim() });
      setTestResult(data);
      if (data.ok) toast.success("Test email sent!");
      else toast.error("Test failed — see details below");
    } catch (err) {
      setTestResult({ ok: false, error: err.response?.data?.detail || err.message });
      toast.error("Test failed");
    }
    setTesting(false);
  };

  const sendPush = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) { toast.error("Title and message are required"); return; }
    setPushSending(true);
    try {
      const { data } = await api(token).post("/push/send", {
        title: pushTitle.trim(),
        body: pushBody.trim(),
        url: pushUrl || "/dashboard",
      });
      toast.success(`Push sent to ${data.sent} of ${data.total} device(s)`);
      setPushTitle(""); setPushBody("");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Push failed");
    }
    setPushSending(false);
  };

  const sendNotification = async (clientId, type, planType, planName) => {
    setSending(`${clientId}-${type}`);
    try {
      const { data } = await api(token).post("/notifications/send", { client_id: clientId, type, plan_type: planType, plan_name: planName });
      if (data.email_sent) toast.success("Email sent!");
      if (data.whatsapp_url) {
        window.open(data.whatsapp_url, "_blank");
        toast.success("WhatsApp opened — send the message!");
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    }
    setSending("");
  };

  const sendBulkReminders = async () => {
    setBulkSending(true);
    try {
      const { data } = await api(token).post("/notifications/send-bulk", { type: "payment_reminder" });
      toast.success(`Notified ${data.sent} members`);
      if (data.whatsapp_links?.length > 0) {
        data.whatsapp_links.forEach((l, i) => {
          setTimeout(() => window.open(l.url, "_blank"), i * 1500);
        });
      }
      loadData();
    } catch { toast.error("Bulk send failed"); }
    setBulkSending(false);
  };

  const updateSetting = async (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    try {
      await api(token).put("/notifications/settings", updated);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
  };

  const needsAttention = clients.filter(c => c.payment_status === "inactive" || c.payment_status === "expired");

  return (
    <div className="space-y-6" data-testid="notifications-tab">
      {/* Quick Actions */}
      <div className="bg-gg-surface border border-white/10 rounded-xl p-5 space-y-4">
        <h3 className="font-heading text-sm uppercase tracking-wider font-bold text-white flex items-center gap-2"><Bell size={16} className="text-gg-red" /> Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <Button onClick={sendBulkReminders} disabled={bulkSending} className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg text-xs font-heading uppercase tracking-wider" data-testid="bulk-reminder-btn">
            <Send size={14} className="mr-1.5" /> {bulkSending ? "Sending..." : `Remind All Pending (${needsAttention.length})`}
          </Button>
          <Button onClick={() => setShowSettings(!showSettings)} variant="outline" className="border-white/10 text-zinc-400 hover:text-white rounded-lg text-xs font-heading uppercase tracking-wider" data-testid="notification-settings-btn">
            <Settings size={14} className="mr-1.5" /> Auto-Reminders
          </Button>
          <Button onClick={() => setShowPush(!showPush)} variant="outline" className="border-white/10 text-zinc-400 hover:text-gg-red hover:border-gg-red/50 rounded-lg text-xs font-heading uppercase tracking-wider" data-testid="push-toggle-btn">
            <Smartphone size={14} className="mr-1.5" /> Push ({pushSubscribers.reduce((a, b) => a + (b.devices || 0), 0)} devices)
          </Button>
          <Button onClick={() => setShowDiag(!showDiag)} variant="outline" className="border-white/10 text-zinc-400 hover:text-white rounded-lg text-xs font-heading uppercase tracking-wider" data-testid="email-diag-btn">
            <Mail size={14} className="mr-1.5" /> Email Diag
          </Button>
        </div>
        {showDiag && (
          <div className="border border-white/5 rounded-lg bg-gg-black p-4 space-y-3 mt-3" data-testid="email-diag-panel">
            <h4 className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-1.5"><Mail size={12} className="text-gg-red" /> Email Diagnostics</h4>
            {diag ? (
              <div className="text-[11px] grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5">
                <div className="flex justify-between border-b border-white/5 pb-1">
                  <span className="text-zinc-500">Resend API Key</span>
                  <span className={diag.resend_api_key_set ? "text-green-400" : "text-red-400"}>{diag.resend_api_key_set ? `Set (${diag.resend_api_key_prefix})` : "Not set"}</span>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-1">
                  <span className="text-zinc-500">Sender Email</span>
                  <span className="text-white">{diag.sender_email}</span>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-1">
                  <span className="text-zinc-500">Site URL</span>
                  <span className="text-white truncate ml-2">{diag.site_url}</span>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-1">
                  <span className="text-zinc-500">Dashboard URL</span>
                  <span className="text-white truncate ml-2">{diag.dashboard_url}</span>
                </div>
              </div>
            ) : (
              <p className="text-zinc-500 text-[11px]">Loading config…</p>
            )}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
              <Input type="email" placeholder="your.email@example.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-8 text-xs flex-1 min-w-[200px]" data-testid="email-diag-input" />
              <Button onClick={runEmailTest} disabled={testing} size="sm" className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg text-xs font-heading uppercase tracking-wider" data-testid="email-diag-send">
                <Send size={12} className="mr-1.5" /> {testing ? "Sending..." : "Send Test"}
              </Button>
            </div>
            {testResult && (
              <div className={`text-[11px] rounded-lg p-3 border ${testResult.ok ? "bg-green-500/5 border-green-500/30" : "bg-red-500/5 border-red-500/30"}`} data-testid="email-diag-result">
                {testResult.ok ? (
                  <>
                    <p className="text-green-400 font-heading uppercase tracking-wider mb-1">✓ Success</p>
                    <p className="text-zinc-400">Resend ID: <span className="text-white">{testResult.resend_id}</span></p>
                    <p className="text-zinc-400">From: <span className="text-white">{testResult.sender}</span> → To: <span className="text-white">{testResult.to}</span></p>
                  </>
                ) : (
                  <>
                    <p className="text-red-400 font-heading uppercase tracking-wider mb-1">✕ Failed</p>
                    <p className="text-zinc-400 break-all">Error: <span className="text-white">{testResult.error}</span></p>
                    {testResult.config && <p className="text-zinc-500 text-[10px] mt-1">Sender: {testResult.config.sender} | Site: {testResult.config.site_url}</p>}
                  </>
                )}
              </div>
            )}
          </div>
        )}
        {showPush && (
          <div className="border border-white/5 rounded-lg bg-gg-black p-4 space-y-3 mt-3" data-testid="push-panel">
            <div className="flex items-center justify-between">
              <h4 className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-1.5"><Smartphone size={12} className="text-gg-red" /> Send Push Notification</h4>
              <span className="text-[10px] text-zinc-500">{pushSubscribers.length} member(s), {pushSubscribers.reduce((a, b) => a + (b.devices || 0), 0)} device(s)</span>
            </div>
            {pushSubscribers.length === 0 ? (
              <p className="text-zinc-500 text-[11px]">No members have enabled push yet. They can turn it on from their dashboard (top-right Bell icon).</p>
            ) : (
              <>
                <Input placeholder="Title (e.g. New plan assigned!)" value={pushTitle} onChange={(e) => setPushTitle(e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-9 text-sm" data-testid="push-title-input" />
                <Textarea placeholder="Message body..." rows={2} value={pushBody} onChange={(e) => setPushBody(e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg text-sm resize-none" data-testid="push-body-input" />
                <div className="flex flex-wrap gap-2 items-center">
                  <Input placeholder="/dashboard" value={pushUrl} onChange={(e) => setPushUrl(e.target.value)} className="bg-gg-surface border-white/10 text-white rounded-lg h-8 text-xs max-w-[200px]" />
                  <span className="text-zinc-600 text-[10px]">← destination path on tap</span>
                </div>
                <Button onClick={sendPush} disabled={pushSending} className="bg-gg-red hover:bg-gg-red-hover text-white rounded-lg text-xs font-heading uppercase tracking-wider" data-testid="push-send-btn">
                  <Send size={12} className="mr-1.5" /> {pushSending ? "Sending..." : "Send to All Subscribed"}
                </Button>
              </>
            )}
            {pushHistory.length > 0 && (
              <div className="border-t border-white/5 pt-3 mt-2">
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-heading mb-2">Recent Pushes</p>
                <div className="space-y-1 max-h-[160px] overflow-y-auto">
                  {pushHistory.slice(0, 10).map((h) => (
                    <div key={h.id} className="text-[11px] flex items-center justify-between gap-2">
                      <span className="text-zinc-300 truncate">{h.title}</span>
                      <span className="text-zinc-600 shrink-0">{h.sent}/{h.target_count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {showSettings && settings && (
          <div className="border border-white/5 rounded-lg bg-gg-black p-4 space-y-3 mt-3">
            <div className="flex items-center justify-between">
              <h4 className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-1.5"><Zap size={12} /> Automated Reminders</h4>
              <button onClick={() => updateSetting("auto_reminders_enabled", !settings.auto_reminders_enabled)}
                className={`flex items-center gap-2 text-[10px] font-heading uppercase tracking-wider px-3 py-1.5 rounded-lg transition-all ${settings.auto_reminders_enabled ? "bg-green-500/10 text-green-400" : "bg-zinc-800 text-zinc-500"}`}
                data-testid="auto-reminders-toggle">
                {settings.auto_reminders_enabled ? "ON" : "OFF"}
                <div className={`w-7 h-4 rounded-full relative transition-colors ${settings.auto_reminders_enabled ? "bg-green-500" : "bg-zinc-700"}`}>
                  <div className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-all ${settings.auto_reminders_enabled ? "left-3.5" : "left-0.5"}`} />
                </div>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[9px] uppercase text-zinc-600 block mb-1">Days Before Renewal</label>
                <select value={settings.days_before_renewal || 3} onChange={e => updateSetting("days_before_renewal", parseInt(e.target.value))}
                  className="w-full bg-gg-surface border border-white/10 text-white rounded-lg h-8 px-2 text-[11px]" data-testid="days-before-select">
                  <option value={1}>1 day before</option>
                  <option value={2}>2 days before</option>
                  <option value={3}>3 days before</option>
                  <option value={5}>5 days before</option>
                  <option value={7}>7 days before</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={settings.remind_on_expiry !== false} onChange={e => updateSetting("remind_on_expiry", e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-white/20 bg-gg-surface accent-[#FF3B30]" />
                <label className="text-zinc-400 text-[10px]">Remind on expiry day</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={settings.remind_inactive !== false} onChange={e => updateSetting("remind_inactive", e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-white/20 bg-gg-surface accent-[#FF3B30]" />
                <label className="text-zinc-400 text-[10px]">Monthly reminder for inactive</label>
              </div>
            </div>
            <p className="text-zinc-600 text-[10px]">Emails are sent automatically every 6 hours. WhatsApp reminders require manual action via the buttons above.</p>
          </div>
        )}
        {needsAttention.length === 0 && <p className="text-green-400 text-xs flex items-center gap-1"><CheckCircle size={12} /> All members have active payments</p>}
      </div>

      {/* Members needing attention */}
      {needsAttention.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-heading text-xs uppercase tracking-wider text-zinc-400">Members Needing Attention ({needsAttention.length})</h3>
          {needsAttention.map(c => (
            <div key={c.id} className="bg-gg-surface border border-white/10 rounded-xl p-4 flex items-center gap-4">
              <div className="w-9 h-9 rounded-full bg-gg-red/10 flex items-center justify-center shrink-0 overflow-hidden">
                {c.profile_image ? <img src={c.profile_image} alt={c.name} className="w-full h-full object-cover" /> : <span className="text-gg-red text-sm font-bold">{c.name?.[0]?.toUpperCase()}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold truncate">{c.name}</p>
                <p className="text-zinc-500 text-[10px]">{c.email} | {c.phone}</p>
              </div>
              <span className={`text-[9px] px-2 py-0.5 rounded-full font-heading uppercase ${c.payment_status === "expired" ? "bg-red-500/10 text-red-400" : "bg-yellow-500/10 text-yellow-400"}`}>
                {c.payment_status || "inactive"}
              </span>
              <div className="flex gap-1.5">
                <Button size="sm" onClick={() => sendNotification(c.id, "payment_reminder")}
                  disabled={sending === `${c.id}-payment_reminder`}
                  className="bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded-lg text-[10px] h-8 px-3"
                  data-testid={`remind-${c.id}`}>
                  <Mail size={12} className="mr-1" /> {sending === `${c.id}-payment_reminder` ? "..." : "Email"}
                </Button>
                <Button size="sm" onClick={() => sendNotification(c.id, "payment_reminder")}
                  disabled={sending === `${c.id}-payment_reminder`}
                  className="bg-green-600/20 text-green-400 hover:bg-green-600/30 rounded-lg text-[10px] h-8 px-3"
                  data-testid={`whatsapp-${c.id}`}>
                  <MessageCircle size={12} className="mr-1" /> WhatsApp
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* All Members — Quick Notify */}
      <div className="space-y-2">
        <h3 className="font-heading text-xs uppercase tracking-wider text-zinc-400">All Members ({clients.length})</h3>
        <div className="grid gap-2 max-h-[300px] overflow-y-auto">
          {clients.map(c => (
            <div key={c.id} className="bg-gg-surface border border-white/5 rounded-lg px-4 py-2.5 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <span className="text-white text-xs font-semibold">{c.name}</span>
                <span className="text-zinc-600 text-[10px] ml-2">{c.email}</span>
              </div>
              <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${c.payment_status === "active" ? "bg-green-500/10 text-green-400" : c.payment_status === "expired" ? "bg-red-500/10 text-red-400" : "bg-zinc-800 text-zinc-500"}`}>
                {c.payment_status || "inactive"}
              </span>
              <div className="flex gap-1">
                <button onClick={() => sendNotification(c.id, "welcome")} className="text-zinc-600 hover:text-blue-400 transition-colors" title="Send Welcome">
                  <Mail size={13} />
                </button>
                <button onClick={() => sendNotification(c.id, "payment_reminder")} className="text-zinc-600 hover:text-yellow-400 transition-colors" title="Payment Reminder">
                  <AlertCircle size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Notification History */}
      <div className="space-y-2">
        <h3 className="font-heading text-xs uppercase tracking-wider text-zinc-400">Recent Notifications ({history.length})</h3>
        {history.length === 0 ? (
          <p className="text-zinc-600 text-xs py-4 text-center">No notifications sent yet</p>
        ) : (
          <div className="max-h-[250px] overflow-y-auto space-y-1.5">
            {history.map((h, i) => (
              <div key={i} className="bg-gg-surface border border-white/5 rounded-lg px-4 py-2.5 flex items-center gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center ${h.type === "welcome" ? "bg-blue-500/10" : h.type?.includes("expired") ? "bg-red-500/10" : "bg-yellow-500/10"}`}>
                  {h.type === "welcome" ? <CheckCircle size={12} className="text-blue-400" /> : h.type === "plan_assigned" ? <Send size={12} className="text-green-400" /> : <AlertCircle size={12} className="text-yellow-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs">{h.client_name} — <span className="text-zinc-400">{h.type?.replace(/_/g, " ")}</span>
                    {h.auto && <span className="ml-1.5 text-[8px] px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded-full">Auto</span>}
                  </p>
                  <p className="text-zinc-600 text-[10px]">
                    {h.email_sent && <span className="text-blue-400 mr-2">Email sent</span>}
                    {new Date(h.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
